import React, { useState, useCallback, useRef, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNavigatorProfile } from '../context/NavigatorContext';
import ForcePasswordChange from './ForcePasswordChange';
import AccountMenu from './AccountMenu';
import NotificationBell from './NotificationBell';
import ThemeToggle from './ThemeToggle';
import { navigatorAPI } from '../services/api';
import { resolveNavigatorId } from '../utils/currentNavigator';
import Icon from './Icon';
import previuLogo from '../assets/previu-logo-white.png';

const NAV = [
  // ── Navigator + Admin only ───────────────────────────────────────────────
  { to: '/dashboard',       icon: 'chart', label: 'Dashboard',      section: null,         hideForDoctor: true },
  { to: '/escalations',     icon: 'alert', label: 'Recommendation and Escalations', section: null,     navigatorOnly: true },
  { to: '/quality-check',   icon: 'clipboard', label: 'Quality check', section: null,       navigatorOrAdminOnly: true },
  // Front-desk queue: ADMIN / HEALTH_NAVIGATOR only. SUPPORT_STAFF is excluded until the
  // role policy is decided: the page calls GET /doctors and /patients/search, which the
  // backend refuses (403) for that role, and a 403 would force a logout.
  { to: '/clinic/queue',    icon: 'clock', label: "Today's clinic", section: null,         navigatorOrAdminOnly: true },
  { to: '/patients',        icon: 'people', label: 'Patients',        section: 'Clinical',   hideForDoctor: true },
  { to: '/ctc-screenings',  icon: 'clipboard', label: 'CTC Screenings', section: 'Clinical', hideForDoctor: true },
  { to: '/appointments',    icon: 'calendar', label: 'Appointments',    section: null,         hideForDoctor: true },
  { to: '/lab-tests',       icon: 'vial', label: 'Lab Tests',       section: null,         hideForDoctor: true },
  { to: '/doctors',         icon: 'hospital', label: 'Doctors',         section: 'Management', hideForDoctor: true },
  { to: '/users',           icon: 'person', label: 'Users',           section: null,         adminOnly: true },
  // ADMIN / HEALTH_NAVIGATOR only, to match the backend rule — SUPPORT_STAFF would 403,
  // and the axios interceptor treats a 403 as an expired session and logs them out.
  { to: '/organizations',   icon: 'people', label: 'Organisations',   section: 'Management', navigatorOrAdminOnly: true },

  // ── Navigator only (not Admin/SUPPORT_STAFF — this is a personal
  //    schedule, like the doctor-only "My Availability" item below) ────────
  { to: '/navigator/availability', icon: 'calendar', label: 'My Availability', section: null, navigatorOnly: true },

  // ── Doctor only ──────────────────────────────────────────────────────────
  { to: '/doctor/home',          icon: 'stethoscope', label: 'Dashboard',      section: 'Clinical', doctorOnly: true },
  { to: '/doctor/patients',      icon: 'people', label: 'My Patients',    section: null,       doctorOnly: true },
  { to: '/doctor/appointments',  icon: 'calendar', label: 'Appointments',   section: null,       doctorOnly: true },
  { to: '/doctor/escalations',   icon: 'alert', label: 'Recommendation and Escalations', section: null,     doctorOnly: true },
  { to: '/doctor/availability',  icon: 'calendar', label: 'My Availability', section: null,    doctorOnly: true },

  // ── Team Chat (Navigator↔Doctor) — one nav item, two routes ─────────────
  { to: '/team-chat',        icon: 'chat', label: 'Team Chat', section: null, hideForDoctor: true },
  { to: '/doctor/team-chat', icon: 'chat', label: 'Team Chat', section: null, doctorOnly: true },

  // ── Patient Messages (Patient↔Navigator / Patient↔Doctor inbox) ─────────
  { to: '/patient-messages',        icon: 'mail', label: 'Patient Messages', section: null, hideForDoctor: true },
  { to: '/doctor/patient-messages', icon: 'mail', label: 'Patient Messages', section: null, doctorOnly: true },

  // ── Shared ───────────────────────────────────────────────────────────────
  { to: '/settings', icon: 'settings', label: 'Settings', section: null },
];

// Bottom tab items for doctor mobile — kept minimal (3 items max per HIG)
const DOCTOR_BOTTOM_TABS = [
  { to: '/doctor/patients',     icon: 'people', label: 'Patients'     },
  { to: '/doctor/appointments', icon: 'calendar', label: 'Appointments' },
  // Short label so it fits the five-item bar; fullLabel is the accessible name.
  { to: '/doctor/escalations',  icon: 'alert', label: 'Recs & Esc.', fullLabel: 'Recommendation and Escalations' },
  { to: '/settings',            icon: 'settings', label: 'Settings'     },
];

export function Topbar({ breadcrumbs = [], onMenuToggle, isDoctor = false }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { theme, refreshProfile } = useNavigatorProfile();

  const handleThemeChange = async next => {
    const userId = await resolveNavigatorId(user);
    await navigatorAPI.update(userId, { theme: next });
    refreshProfile();
  };

  return (
    <header className="topbar">
      {!isDoctor && (
        <button
          className="hamburger-btn"
          onClick={onMenuToggle}
          aria-label="Toggle navigation menu"
          type="button"
        >
          ☰
        </button>
      )}
      {isDoctor && (
        <button
          className="hamburger-btn hamburger-btn--desktop-only"
          onClick={onMenuToggle}
          aria-label="Toggle navigation menu"
          type="button"
        >
          ☰
        </button>
      )}

      <div className="topbar-logo">
        <img src={previuLogo} alt="Previu" style={{ height: 34, width: 'auto' }} />
      </div>

      <nav className="topbar-breadcrumb">
        {breadcrumbs.map((crumb, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span className="crumb-sep">/</span>}
            {i === breadcrumbs.length - 1
              ? <span className="crumb-active">{crumb.label}</span>
              : <span
                  style={{ cursor: 'pointer' }}
                  onClick={() => crumb.to && navigate(crumb.to)}
                >{crumb.label}</span>
            }
          </React.Fragment>
        ))}
      </nav>

      <div className="topbar-actions">
        <NotificationBell role={user?.role === 'DOCTOR' ? 'DOCTOR' : 'NAVIGATOR'}
          accountRole={user?.role}
          canReview={user?.role === 'DOCTOR' || user?.role === 'HEALTH_NAVIGATOR'}
          canFollowUp={user?.role === 'HEALTH_NAVIGATOR'}
          canEscalate={['DOCTOR', 'HEALTH_NAVIGATOR', 'ADMIN'].includes(user?.role)} />
        <ThemeToggle isDark={theme !== 'LIGHT'} onChange={handleThemeChange} />
        <button className="topbar-icon-btn" title="Help" aria-label="Help"><Icon name="help" size={17} /></button>
        <AccountMenu
          initials={
            ((user?.firstName?.[0] || '') + (user?.lastName?.[0] || ''))
            || (user?.name?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'NA')
          }
          name={user?.name || user?.email}
          role={user?.role === 'DOCTOR' ? 'Doctor'
            : user?.role === 'ADMIN' ? 'Admin' : 'Health Navigator'}
          secondary={user?.email}
          settingsTo="/settings"
          onLogout={logout}
        />
      </div>
    </header>
  );
}

export function Sidebar({ isOpen, onClose }) {
  const { user, logout } = useAuth();
  const isAdmin     = user?.role === 'ADMIN';
  const isNavigator = user?.role === 'HEALTH_NAVIGATOR';
  const isDoctor    = user?.role === 'DOCTOR';
  let lastSection = null;

  return (
    <aside className={`sidebar${isOpen ? ' open' : ''}`}>
      {NAV
        .filter(item => {
          if (item.adminOnly    && !isAdmin)  return false;
          if (item.doctorOnly   && !isDoctor) return false;
          if (item.hideForDoctor && isDoctor) return false;
          if (item.navigatorOrAdminOnly && !isAdmin && !isNavigator) return false;
          if (item.navigatorOnly && !isNavigator) return false;
          return true;
        })
        .map((item) => {
          const showSection = item.section && item.section !== lastSection;
          if (item.section) lastSection = item.section;

          return (
            <React.Fragment key={item.to}>
              {showSection && (
                <div className="sidebar-section">{item.section}</div>
              )}
              <NavLink
                to={item.to}
                className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
                onClick={onClose}
              >
                <span className="nav-icon"><Icon name={item.icon} /></span>
                {item.label}
              </NavLink>
            </React.Fragment>
          );
        })}

      <div style={{ marginTop: 'auto', borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: 12 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '8px 12px', marginBottom: 4,
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: '50%',
            background: 'linear-gradient(135deg, var(--teal), var(--brand-navy))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 600, color: 'var(--text-on-brand-dark)', flexShrink: 0,
          }}>
            {(user?.name || user?.email || 'U').charAt(0).toUpperCase()}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{
              fontSize: 13, fontWeight: 500, color: 'var(--text-on-brand-dark)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {user?.name || user?.email || 'User'}
            </div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>
              {user?.role === 'DOCTOR' ? (user?.specialization || 'Doctor')
                : user?.role === 'ADMIN' ? 'Admin'
                : 'Health Navigator'}
            </div>
          </div>
        </div>
        <button
          onClick={logout}
          className="nav-item"
          style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer',
            textAlign: 'left', color: 'rgba(255,100,100,0.7)', fontFamily: 'inherit' }}
        >
          <span className="nav-icon"><Icon name="logout" /></span>
          Sign out
        </button>
      </div>
    </aside>
  );
}

// ── Doctor bottom tab bar — mobile only (<768px) ──────────────────────────────
export function DoctorBottomTabBar() {
  const { logout } = useAuth();

  return (
    <nav className="doctor-bottom-tabs" aria-label="Doctor navigation">
      {DOCTOR_BOTTOM_TABS.map(tab => (
        <NavLink
          key={tab.to}
          to={tab.to}
          aria-label={tab.fullLabel}
          className={({ isActive }) =>
            `mobile-tab-item${isActive ? ' mobile-tab-item--active' : ''}`
          }
        >
          <span className="mobile-tab-icon" aria-hidden="true"><Icon name={tab.icon} /></span>
          <span className="mobile-tab-label">{tab.label}</span>
        </NavLink>
      ))}

      <button
        className="mobile-tab-item mobile-tab-item--signout"
        onClick={logout}
        aria-label="Sign out"
      >
        <span className="mobile-tab-icon" aria-hidden="true"><Icon name="logout" /></span>
        <span className="mobile-tab-label">Sign out</span>
      </button>
    </nav>
  );
}

export function AppShell({ children, breadcrumbs }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const { user } = useAuth();
  const isDoctor = user?.role === 'DOCTOR';
  const { theme } = useNavigatorProfile();

  const toggleMenu = useCallback(() => setMenuOpen(prev => !prev), []);
  const closeMenu  = useCallback(() => setMenuOpen(false), []);

  if (user?.mustChangePassword) {
    return <ForcePasswordChange />;
  }

  return (
    <div className="app-shell" data-theme={theme === 'LIGHT' ? 'light' : 'dark'}>
      <Topbar breadcrumbs={breadcrumbs} onMenuToggle={toggleMenu} isDoctor={isDoctor} />
      <Sidebar isOpen={menuOpen} onClose={closeMenu} />
      {menuOpen && (
        <div className="sidebar-overlay" onClick={closeMenu} aria-hidden="true" />
      )}
      <main className={`main-content${isDoctor ? ' main-content--doctor' : ''}`}>
        {children}
      </main>
      {isDoctor && <DoctorBottomTabBar />}
    </div>
  );
}